import React, { useEffect, useRef } from 'react';
import { getCurrentUserAttributes, federatedSignIn } from '../../sso/components/oauth/auth-provider-methods';
import { hasValue } from '../../../general/js/general-helpers';

const GAME_TOKEN_ENDPOINT = '/api/SIFantasyTokenApi/GetGameToken';
const BODY_ONLY_CLASS = 'si-fantasy-body-only';
const REFRESH_BUFFER_SECONDS = 60;
const DEFAULT_REFRESH_SECONDS = 55 * 60;

export function SIFantasyGamesLayout(props) {
    const {
        allowedUserAttributes,
        storageName,
        gameComponentName,
        loginEventName,
        showBodyOnly
    } = props;

    const refreshTimer = useRef(null);

    useEffect(() => {
        let cancelled = false;

        const scheduleRefresh = (expiresInSeconds) => {
            const delaySeconds = Math.max(
                30,
                (expiresInSeconds || DEFAULT_REFRESH_SECONDS) - REFRESH_BUFFER_SECONDS
            );
            refreshTimer.current = setTimeout(loadToken, delaySeconds * 1000);
        };

        async function loadToken() {
            try {
                const user = await getCurrentUserAttributes();
                if (cancelled || !hasValue(user) || !hasValue(user.idToken)) {
                    return;
                }

                const response = await fetch(GAME_TOKEN_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        idToken: user.idToken,
                        allowedAttributes: allowedUserAttributes
                    })
                });

                if (!response.ok) {
                    throw new Error(`Token endpoint returned ${response.status}`);
                }

                const { token, expiresInSeconds } = await response.json();
                if (cancelled || !hasValue(token)) {
                    return;
                }

                localStorage.setItem(storageName, token);
                window.sifantasystorageKey = storageName;

                scheduleRefresh(expiresInSeconds);
            } catch (error) {
                console.error('Failed to load fantasy game token', error);
            }
        }

        loadToken();

        return () => {
            cancelled = true;
            if (refreshTimer.current) {
                clearTimeout(refreshTimer.current);
            }
        };
    }, [allowedUserAttributes, storageName]);

    useEffect(() => {
        if (!hasValue(loginEventName)) {
            return undefined;
        }

        const handleLoginRequest = async () => {
            const user = await getCurrentUserAttributes();
            if (!hasValue(user) || !hasValue(user.idToken)) {
                federatedSignIn();
            }
        };

        window.addEventListener(loginEventName, handleLoginRequest);

        return () => window.removeEventListener(loginEventName, handleLoginRequest);
    }, [loginEventName]);

    useEffect(() => {
        if (!hasValue(showBodyOnly)) {
            return undefined;
        }

        const [paramKey, paramValue] = showBodyOnly.split('=');
        if (!paramKey) {
            return undefined;
        }

        const matches = new URLSearchParams(window.location.search).get(paramKey) === paramValue;
        if (!matches) {
            return undefined;
        }

        document.body.classList.add(BODY_ONLY_CLASS);

        return () => document.body.classList.remove(BODY_ONLY_CLASS);
    }, [showBodyOnly]);

    return <div id={gameComponentName} />;
}
